/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/models/mediaFile.ts
 */

import type { IMediaFile, StoredMediaFile } from '@repo/common';
import { extractPathComponents } from '@repo/toolslib/src/utils/path.util';
import { getHandler } from '../sharedlib';
import { separator } from '../utils/constants';

export class MediaFile implements IMediaFile {
  url: URL;

  readonly transformedPath: string; // 16-bit-wav/foo-tmp.wav
  readonly cleanedPath: string; // 16-bit-wav/foo-tmp.mp3
  readonly backupPath: string; // 16-bit-wav/foo-bak.mp3
  readonly combinedRecPath: string; // recordings/foo.mp4
  readonly tempSystemRecPath: string; // recordings/foo.mp3.temp/sys.wav
  readonly tempMicRecPath: string; // recordings/foo.mp3.temp/mic.wav
  originalPath: string; // recordings/foo.mp3

  /*
  path: The path to the file, e.g. $AppCache/recordings/foo.mp3
  transformedPathDir: The wav directory, e.g. $AppCache/16-bit-wav/
  recordingsPathDir: The recordings directory, e.g. $AppCache/recordings
   */
  constructor(path: string, transformedPathDir: string, recordingsPathDir: string) {
    console.log('[MediaFile] Constructor called with:', {
      path,
      transformedPathDir,
      recordingsPathDir
    });
    this.originalPath = path;
    this.url = new URL('file://' + path);

    // Ensure directories end with a separator
    // Use the separator that's already in the path (backslash on Windows, forward slash elsewhere)
    const normalizedTransformedDir = transformedPathDir.endsWith(separator)
      ? transformedPathDir
      : transformedPathDir + separator;

    const normalizedRecordingsDir = recordingsPathDir.endsWith(separator)
      ? recordingsPathDir
      : recordingsPathDir + separator;

    // Extract filename directly from the path parameter to avoid circular dependency
    let filename: string;
    try {
      const pathComponents = extractPathComponents(path);
      filename = pathComponents.filename;
      console.log('[MediaFile] Extracted filename:', filename, 'from path:', path);

      if (!filename) {
        console.error('[MediaFile] ERROR: filename is undefined/empty!', { path, pathComponents });
        throw new Error(`Failed to extract filename from path: ${path}`);
      }
    } catch (error) {
      console.error('[MediaFile] ERROR: Failed to extract path components from:', path, error);
      throw error;
    }

    this.transformedPath = normalizedTransformedDir + filename + '-tmp.wav';
    this.cleanedPath = normalizedTransformedDir + filename + '-tmp.mp3';
    this.backupPath = normalizedTransformedDir + filename + '-bak.mp3';
    this.tempSystemRecPath = normalizedRecordingsDir + filename + '.rt' + separator + 'sys.wav';
    this.tempMicRecPath = normalizedRecordingsDir + filename + '.rt' + separator + 'mic.wav';
    this.combinedRecPath = normalizedRecordingsDir + filename + '.mp4';

    console.log('[MediaFile] Constructed paths:', {
      transformedPath: this.transformedPath,
      tempSystemRecPath: this.tempSystemRecPath,
      tempMicRecPath: this.tempMicRecPath
    });
  }

  /**
   * Creates a MediaFile from stored data, using directory fallbacks for missing fields
   * This allows us to change default directories for future files without harming existing ones
   */
  static fromStored(
    storedData: StoredMediaFile,
    waveDir: string,
    recordingsDir: string
  ): MediaFile {
    console.log('[MediaFile] fromStored called with:', {
      originalPath: storedData.originalPath,
      waveDir,
      recordingsDir,
      hasTransformedPath: !!storedData.transformedPath,
      hasTempSystemRecPath: !!storedData.tempSystemRecPath
    });

    // Validate originalPath before creating MediaFile
    if (!storedData.originalPath || storedData.originalPath.includes('undefined')) {
      throw new Error(
        `[MediaFile] Cannot create MediaFile from invalid originalPath: ${storedData.originalPath}`
      );
    }

    // Create a new MediaFile instance with stored data or fallbacks
    const mediaFile = new MediaFile(storedData.originalPath, waveDir, recordingsDir);

    // Override computed paths with stored data if available
    // BUT skip paths that contain "undefined" (from old buggy data)
    if (storedData.transformedPath && !storedData.transformedPath.includes('undefined')) {
      (mediaFile as any).transformedPath = storedData.transformedPath;
    }
    if (storedData.cleanedPath && !storedData.cleanedPath.includes('undefined')) {
      (mediaFile as any).cleanedPath = storedData.cleanedPath;
    }
    if (storedData.backupPath && !storedData.backupPath.includes('undefined')) {
      (mediaFile as any).backupPath = storedData.backupPath;
    }
    if (storedData.combinedRecPath && !storedData.combinedRecPath.includes('undefined')) {
      (mediaFile as any).combinedRecPath = storedData.combinedRecPath;
    } else if (storedData.combinedRecPath?.includes('undefined')) {
      console.warn(
        '[MediaFile] Skipping bad combinedRecPath from storage:',
        storedData.combinedRecPath
      );
    }
    if (storedData.tempSystemRecPath && !storedData.tempSystemRecPath.includes('undefined')) {
      (mediaFile as any).tempSystemRecPath = storedData.tempSystemRecPath;
    } else if (storedData.tempSystemRecPath?.includes('undefined')) {
      console.warn(
        '[MediaFile] Skipping bad tempSystemRecPath from storage:',
        storedData.tempSystemRecPath
      );
    }
    if (storedData.tempMicRecPath && !storedData.tempMicRecPath.includes('undefined')) {
      (mediaFile as any).tempMicRecPath = storedData.tempMicRecPath;
    } else if (storedData.tempMicRecPath?.includes('undefined')) {
      console.warn(
        '[MediaFile] Skipping bad tempMicRecPath from storage:',
        storedData.tempMicRecPath
      );
    }

    return mediaFile;
  }

  /**
   * Serializes the MediaFile for storage
   * This preserves all computed paths so they can be restored later
   */
  toStored(): StoredMediaFile {
    return {
      originalPath: this.originalPath,
      url: this.url.toString(),
      transformedPath: this.transformedPath,
      cleanedPath: this.cleanedPath,
      backupPath: this.backupPath,
      combinedRecPath: this.combinedRecPath,
      tempSystemRecPath: this.tempSystemRecPath,
      tempMicRecPath: this.tempMicRecPath
    };
  }

  // All created file paths; used for deletion
  get allFilePaths(): string[] {
    return [
      this.originalPath,
      this.transformedPath,
      this.cleanedPath,
      this.combinedRecPath,
      this.tempMicRecPath,
      this.tempSystemRecPath
    ];
  }

  // The filename without the extension
  get name() {
    const pathInfo = extractPathComponents(this.path);
    return pathInfo.filename;
  }

  // Filename extension, excluding the dot, e.g. "mp3"
  get extension() {
    const pathInfo = extractPathComponents(this.path);
    return (pathInfo.extension || '.').substring(1);
  }

  // The filename including the extension
  get fileName() {
    const pathInfo = extractPathComponents(this.path);
    return `${pathInfo.filename}${pathInfo.extension || ''}`;
  }

  // Decoded Path to Mp3
  get path() {
    const pathname = decodeURI(this.url.pathname);
    // On Windows, URL.pathname returns /C:/Users/... but we need C:/Users/...
    // Remove leading slash if path starts with a drive letter (e.g., /C:/)
    if (pathname.match(/^\/[a-zA-Z]:\//)) {
      return pathname.substring(1);
    }
    return pathname;
  }

  // Asset URL to mp3
  get assetUrl() {
    return 'asset://localhost/' + this.path;
  }

  static async create(path: string): Promise<MediaFile> {
    const waveDir = await getHandler().getWaveDir();
    const recordingsDir = await getHandler().getRecordingsDir();
    return new MediaFile(path, waveDir, recordingsDir);
  }

  static async createAutoNamed(): Promise<MediaFile> {
    const recordingsDir = await getHandler().getRecordingsDir();
    console.log('[MediaFile] createAutoNamed - recordingsDir:', recordingsDir);
    // Ensure recordingsDir ends with a separator for consistent concatenation
    // Use the separator that's already in the path (backslash on Windows, forward slash elsewhere)
    const separator = recordingsDir.includes('\\') ? '\\' : '/';
    const normalizedDir =
      recordingsDir.endsWith('/') || recordingsDir.endsWith('\\')
        ? recordingsDir
        : recordingsDir + separator;
    console.log('[MediaFile] createAutoNamed - normalizedDir:', normalizedDir);
    const path = `${normalizedDir}new-${Date.now()}.mp3`;
    console.log('[MediaFile] createAutoNamed - final path:', path);
    const mediaFile = await MediaFile.create(path);
    if (mediaFile.path.includes('undefined')) {
      throw new Error('New media file path is undefined');
    } else {
      console.log('New media file path is', mediaFile.path);
    }
    return mediaFile;
  }

  /**
   * Test method to verify that fromStored and toStored work correctly together
   */
  static async testStoredRoundTrip(path: string): Promise<boolean> {
    try {
      const original = await MediaFile.create(path);
      const stored = original.toStored();
      const waveDir = await getHandler().getWaveDir();
      const recordingsDir = await getHandler().getRecordingsDir();
      const reconstructed = MediaFile.fromStored(stored, waveDir, recordingsDir);

      // Verify that all paths are preserved
      const pathsMatch =
        original.transformedPath === reconstructed.transformedPath &&
        original.cleanedPath === reconstructed.cleanedPath &&
        original.backupPath === reconstructed.backupPath &&
        original.combinedRecPath === reconstructed.combinedRecPath &&
        original.tempSystemRecPath === reconstructed.tempSystemRecPath &&
        original.tempMicRecPath === reconstructed.tempMicRecPath;

      console.log('MediaFile stored round-trip test:', pathsMatch ? 'PASSED' : 'FAILED');
      return pathsMatch;
    } catch (error) {
      console.error('MediaFile stored round-trip test failed:', error);
      return false;
    }
  }
}
